/**
 * Calendario de vacaciones.
 *
 * Las fechas viajan como texto ISO (yyyy-MM-dd), no como Date: es el
 * formato nativo de <input type="date"> y evita que la zona horaria mueva
 * un día el periodo al serializar. Un día de vacaciones no tiene hora.
 */

import { Permiso } from "../permisos/permiso.model";
import { FrcFirmado } from "../frc/frc-firmado.model";

export interface Vacacion {
  idVacacion?: number;
  idUsuario?: number;
  /** Resuelto por el backend, no se captura. */
  colaborador?: string;
  /** Primer día de descanso. */
  fechaInicio?: string;
  /** Último día de descanso. */
  fechaFin?: string;
  /** Primer día de vuelta. Informativo: no se pinta como vacación. */
  fechaRegreso?: string;
  /** Lo asigna el backend a partir del id del colaborador. */
  color?: string;
  comentario?: string;
  /**
   * PENDIENTE  pedida por el colaborador, esperando respuesta
   * ACTIVO     autorizada; es la que se pinta en el calendario
   * RECHAZADA  no se autorizó, con su motivo
   * CANCELADO  se quitó del calendario
   */
  estatus?: string;
  /** Quién resolvió. Nulo mientras está pendiente. */
  idAutoriza?: number;
  autorizaNombre?: string;
  fechaResolucion?: string;
  /**
   * Sólo en la bandeja de un SUPLENTE: el autorizador de siempre, que hoy
   * no está, y por qué ("de vacaciones hasta el 20/09"). Así quien la
   * recibe sabe que le llega por ausencia de otro. Nulos si es su gente.
   */
  suplenteDe?: string;
  ausencia?: string;
  /**
   * Sólo en la bandeja de QUIEN ADMINISTRA el calendario: le llega
   * porque el colaborador no tiene autorizador (o se tiene a sí mismo),
   * no porque sea su gente. Desde el 14/09 es lo único ajeno que ve.
   */
  sinAutorizador?: boolean;
  /** Por qué no se autorizó. */
  motivo?: string;
  /** CAPTURA (la registró quien administra) o SOLICITUD (la pidió él). */
  origen?: string;
  /**
   * Días sueltos (15/10/2026): cada tramo de días seguidos es su renglón y
   * los del mismo pedido llevan el id del primero. En pantalla son UNA
   * solicitud (ver agrupaPedidos). Nulo en lo de un solo tramo.
   */
  idPedido?: number;
  /** Los tramos del pedido, en orden: los junta agrupaPedidos, y el FRC-019 los trae del servidor. */
  tramos?: Vacacion[];
  idUsuarioAlta?: number;
  usuarioAltaNombre?: string;
  fechaCreacion?: string;
  /** Firma del colaborador al pedir (PNG base64). Va al FRC-019. */
  firmaSolicita?: string;
  /** Firma del líder al autorizar (PNG base64). Sella el FRC-019. */
  firmaAutoriza?: string;
  /** Área del organigrama. Solo viene al pedir el FRC-019. */
  area?: string;
  /** Si el colaborador firmó al pedir: solo entonces existe FRC-019. */
  tieneFirma?: boolean;
  /**
   * Alguna de las dos firmas se hizo en papel («Descargar para firmar»):
   * hay un FRC-019 firmado a mano que subir.
   */
  enPapel?: boolean;
  /** El FRC-019 firmado a mano que ya se subió (el vigente), o nada. */
  firmado?: FrcFirmado | null;
}

export interface Colaborador {
  idUsuario?: number;
  nombre?: string;
  color?: string;
  periodos?: number;
  /** Fecha de ingreso (yyyy-MM-dd), si RH ya la capturó. */
  fechaIngreso?: string;
}

/**
 * Un PERIODO de vacaciones de alguien: cuántos días le tocan, cuántos lleva
 * usados y cuántos trae acumulados de antes.
 *
 * Desde el 24/09/2026 cada saldo es un periodo de aniversario —del
 * aniversario de su Alta IMSS al siguiente—, igual que la hoja de RH; quien
 * no tiene fecha de ingreso sigue con el año calendario. Lo que sobra de un
 * periodo pasa solo al siguiente como acumulado (diasArrastre).
 *
 * El servidor lo manda YA calculado: periodoInicio/periodoFin siempre llenos,
 * restantes y disponibles. La pantalla no calcula periodos (la regla del
 * aniversario y del 29 de febrero vive en VacacionBuss).
 */
export interface Saldo {
  idSaldo?: number;
  idUsuario?: number;
  colaborador?: string;
  /**
   * El año en que EMPIEZA el periodo, no "el año del saldo": el periodo del
   * 18/12/2025 al 18/12/2026 es el 2025. Es la llave para guardar: al
   * corregir un saldo se manda el `anio` del renglón que se enseña, nunca el
   * del mes que se ve.
   */
  anio?: number;
  /** Días de goce del periodo (LFT art. 76): los de ley por la antigüedad al empezar. */
  diasTotales?: number;
  /**
   * Lo calcula el backend. Cuenta lo autorizado Y lo pendiente: una
   * solicitud sin resolver ya aparta los días, o alguien podría pedir
   * treinta en seis solicitudes de cinco sin pasarse en ninguna.
   */
  diasTomados?: number;
  /** Área del organigrama (FOR-016). */
  area?: string;
  /** Quién resuelve las solicitudes de este colaborador. */
  idAutoriza?: number;
  autorizaNombre?: string;
  /**
   * Si hoy su autorizador no está: quién lo suple y por qué. "Esperando
   * a Irving" pasa a "Esperando a Macario (Irving está de vacaciones
   * hasta el 20/09)". Nulos cuando el autorizador está.
   */
  suplenteNombre?: string;
  ausencia?: string;
  comentario?: string;
  /** Fecha de ingreso (yyyy-MM-dd). Opcional; con ella los días salen por ley. */
  fechaIngreso?: string;
  /** Años cumplidos al empezar el periodo. Solo con fecha de ingreso. */
  antiguedad?: number;
  /** Días por ley con esa antigüedad. Solo con fecha de ingreso. */
  diasPorLey?: number;
  usuarioActivo?: boolean;

  /* ---------- Lo que trae el Excel de RH ----------
   *
   * Los cinco son opcionales y nulos mientras nadie suba el archivo: si
   * no se carga nada, las cuentas dan exactamente lo mismo que antes.
   * Nulo NO es cero, igual que con diasTotales: significa "no se sabe". */

  /**
   * Días que ya se tomó FUERA de COS dentro del periodo, según el Excel
   * de RH. Suman a los USADOS y no a los que le tocan: la prima del art.
   * 80 se paga sobre los días del año, no sobre lo que se gozó antes.
   */
  diasGozadosPrevios?: number | null;
  /**
   * Lo ACUMULADO de periodos anteriores: lo que le sobró y no tomó. Lo
   * fija RH («Dias Pend» del Excel) o sale solo del periodo anterior
   * (arrastreCalculado). Suma a lo que LE QUEDA pero no a la prima. Puede
   * ser negativo: quien tomó de más en un periodo lo ve descontado aquí.
   */
  diasArrastre?: number | null;
  /** true si diasArrastre salió del periodo anterior y no de RH. No se manda al guardar. */
  arrastreCalculado?: boolean | null;
  /** Primer día del periodo (yyyy-MM-dd). Siempre lleno. */
  periodoInicio?: string | null;
  /**
   * Primer día que YA NO es del periodo: el aniversario siguiente, como la
   * columna «Fin del periodo» del Excel (intervalo [inicio, fin)).
   */
  periodoFin?: string | null;
  /** true si va por aniversario; false si es el año calendario (sin fecha de ingreso). */
  porAniversario?: boolean | null;
  /** true si el periodo contiene el día de hoy. */
  vigente?: boolean | null;
  /** Días de goce − tomados en COS − gozados fuera de COS («Días restantes» del Excel). */
  restantes?: number | null;
  /** Restantes + acumulado: lo que de verdad puede pedir («acumulados» del Excel). */
  disponibles?: number | null;
  /**
   * A qué día están cuadrados los gozados previos (la fecha de la
   * carga). Sin ella no se sabe hasta dónde manda el Excel y desde
   * dónde manda el calendario de COS.
   */
  fechaCorte?: string | null;

  /* ---------- Lo que se ENSEÑA: «lo que le sobra primero» (20/10/2026) ----------
   *
   * Lo que se pide en un periodo sale primero de lo que sobró de los
   * anteriores. Los de arriba siguen siendo con los que se valida; estos
   * dicen de cuál periodo salen los días. Los calcula el servidor
   * (VacacionBuss.primeroLoQueSobra) y se leen con ../../vacaciones/saldo-vista. */

  /** «Gozados»: lo que sale de los días de goce de este periodo. */
  gozadosVista?: number | null;
  /** «Restantes»: días de goce − gozadosVista. */
  restantesVista?: number | null;
  /** «Acumulado»: lo que sobró de periodos anteriores y no se ha usado. */
  acumuladoVista?: number | null;
  /** «Le quedan»: restantesVista + acumuladoVista. */
  quedanVista?: number | null;
  /** De lo pedido en COS en este periodo, lo que salió de lo acumulado. */
  deLoAcumulado?: number | null;
  /** De lo que le sobraba a este periodo, lo que ya apartaron vacaciones de periodos posteriores. */
  prestados?: number | null;
}

/* =====================================================================
   Carga del Excel de RH
   =====================================================================

   Dos pasos, como el formato FOS-005 de matrices: la previa lee el
   archivo y devuelve este plan SIN escribir nada, y la carga vuelve a
   mandar el mismo archivo con la huella para aplicarlo. Los nombres de
   abajo son los que devuelve el backend tal cual: si cambian allá,
   cambian aquí. */

/**
 * Qué tan seguro es el cruce entre el nombre del Excel y la persona de
 * COS. SIN_RESOLVER es el único que no se puede cargar. MANUAL es la
 * que se asignó a mano en la vista previa (viaja en `asignaciones`).
 */
export type ConfianzaCruce = "EXACTA" | "APELLIDOS" | "TOKENS" | "SIN_RESOLVER" | "MANUAL";

/**
 * Una persona de COS a la que se puede asignar una fila del Excel: los
 * candidatos de la fila y la lista completa de activos del plan.
 */
export interface UsuarioPlan {
  idUsuario: number;
  nombre?: string;
  userNom?: string;
}

/**
 * Qué le va a pasar a esa fila: NUEVO crea el saldo, ACTUALIZA lo
 * corrige, IGUAL no toca nada porque ya está cuadrado y OMITIDA se
 * queda fuera (sin resolver, fila vacía o fuera de la carga).
 */
export type EstadoFilaPlan = "NUEVO" | "ACTUALIZA" | "IGUAL" | "OMITIDA";

/** Una fila del Excel, ya cruzada contra COS y con sus cuentas hechas. */
export interface FilaPlanVacaciones {
  /** Renglón del Excel, para poder señalarlo en la hoja. */
  filaExcel?: number;
  nombreExcel?: string;
  /** El área que dice el Excel. Informativa: NO se guarda. */
  area?: string;
  /** Alta IMSS (yyyy-MM-dd). */
  altaImss?: string;
  /** A quién se resolvió en COS. null cuando no se pudo. */
  idUsuario?: number | null;
  nombreCos?: string | null;
  userNom?: string | null;
  confianza?: ConfianzaCruce;
  /** Años cumplidos y los días que le da la LFT art. 76 con ellos. */
  antiguedad?: number | null;
  diasPorLey?: number | null;
  /** El periodo de aniversario que se calculó (no se copia del Excel). */
  periodoInicio?: string | null;
  periodoFin?: string | null;
  /** Lo que dice la columna "Días gozados". */
  gozadosExcel?: number | null;
  /** Lo que COS ya tiene capturado con fechas hasta la fecha de corte. */
  yaEnCos?: number | null;
  /** El ajuste que se va a guardar: gozadosExcel − yaEnCos, nunca negativo. */
  gozadosPrevios?: number | null;
  /** "Días Pend AAAA" del periodo anterior. */
  arrastre?: number | null;
  /** Días que le tocan antes y después de la carga. */
  diasAntes?: number | null;
  diasDespues?: number | null;

  /* ---------- Desde el 24/09/2026: por periodo de aniversario ---------- */

  /** Lo demás que trae la hoja, para enseñarlo junto a lo de COS (no se copia). */
  inicioExcel?: string | null;
  finExcel?: string | null;
  antiguedadExcel?: number | null;
  goceExcel?: number | null;
  restantesExcel?: number | null;
  acumuladosExcel?: number | null;
  /** La fila viene oculta en el Excel: no se marca sola en la vista previa. */
  oculta?: boolean | null;
  /** El año del periodo de la hoja (en el que empieza): el renglón que se escribe. */
  anioPeriodo?: number | null;
  /** Días que COS ya tiene con fechas dentro de ese periodo. */
  tomadosPeriodo?: number | null;
  /** Días de goce − tomados − gozados («Días restantes»). */
  restantes?: number | null;
  /** Restantes + pendientes («acumulados»). */
  disponibles?: number | null;
  /** El periodo de la hoja ya terminó: al cargar se abre el nuevo con lo que sobró. */
  periodoCerrado?: boolean | null;
  periodoNuevoInicio?: string | null;
  periodoNuevoFin?: string | null;
  antiguedadNueva?: number | null;
  diasNuevo?: number | null;
  acumuladoNuevo?: number | null;
  disponiblesNuevo?: number | null;
  /** Lo que puede pedir HOY antes y después de cargar. */
  disponiblesAntes?: number | null;
  disponiblesDespues?: number | null;
  estado?: EstadoFilaPlan;
  /** Por qué se omite, o qué hay que mirar en esta fila. */
  nota?: string;
  /**
   * Hasta cinco activos de COS que comparten apellido con el nombre del
   * Excel. Solo en las filas SIN_RESOLVER, APELLIDOS o TOKENS: son los
   * primeros que se ofrecen al asignarla a mano.
   */
  candidatos?: UsuarioPlan[];
  /**
   * false si esa persona no tiene el perfil para pedir vacaciones en COS:
   * se le cargan los días igual, pero no podrá pedirlos. Nulo si la fila
   * no se resolvió.
   */
  puedePedir?: boolean;
}

/**
 * Lo que se leyó del Excel y lo que se haría con ello.
 *
 * El backend lo devuelve TAL CUAL en los dos endpoints (no va envuelto
 * en otro bean): en la previa es lo que se haría, y en la carga es lo
 * que ya se aplicó. codigoError/mensajeError son los de siempre y
 * llegan solo cuando algo tronó antes de poder armar el plan.
 */
export interface PlanImportacionVacaciones {
  /** Amarra la previa con la carga: si el archivo cambia, se rechaza. */
  huella?: string;
  /** Ya no decide nada: cada persona va a SU periodo. Solo informativo. */
  anio?: number;
  /**
   * La FECHA DE LA HOJA: el día al que RH la cuadró. Por omisión la fecha
   * en que se guardó el libro (fechaExcel); la pantalla la deja cambiar y
   * la manda igual en la previa y en la carga.
   */
  fechaCorte?: string;
  /** La fecha en que se guardó el libro (.xlsx), si la trae. */
  fechaExcel?: string | null;
  /** false con un solo error: entonces el botón de cargar no se habilita. */
  valido?: boolean;
  /** Lo que IMPIDE cargar. */
  errores?: string[];
  /** Lo que hay que mirar antes de confirmar, pero no bloquea. */
  avisos?: string[];
  filas?: FilaPlanVacaciones[];
  /**
   * Las hojas VISIBLES del libro que traen la tabla, en el orden del libro.
   * Con más de una, la pantalla deja elegir cuál se carga y la manda como
   * `hoja` en la previa y en la carga.
   */
  hojas?: string[];
  /**
   * La hoja que se leyó. Nula cuando el libro trae varias y no se dijo
   * cuál: entonces el plan viene sin filas y con el error de elegirla.
   */
  hoja?: string | null;
  /**
   * Todos los activos de COS (idUsuario, nombre, userNom), para buscar a
   * quién asignar una fila a mano. Solo viene si alguna fila está sin
   * resolver, dudosa o ya asignada a mano.
   */
  usuarios?: UsuarioPlan[];
  codigoError?: string;
  mensajeError?: string;
}

/**
 * El seguimiento de solo lectura tiene dos alcances: EQUIPO (la gente
 * que uno autoriza; es "Mi equipo") y TODOS (la empresa; es el módulo
 * de RH). La pantalla lo pide según el menú por el que entró y el
 * servidor decide si lo concede.
 */
export type AlcanceSeguimiento = "EQUIPO" | "TODOS";

export interface VacacionReq {
  /** Al ver el FRC-019 firmado: uno del historial; sin decir, el que se presenta. */
  idFirmado?: number;
  vacacion?: Vacacion;
  idVacacion?: number;
  desde?: string;
  hasta?: string;
  saldo?: Saldo;
  /** Al resolver: true autoriza, false rechaza. */
  aprobar?: boolean;
  /** Motivo del rechazo. */
  motivo?: string;
  /** Firma del líder al autorizar (PNG base64). */
  firma?: string;
  /** Sólo el seguimiento: qué alcance se pide. */
  alcance?: AlcanceSeguimiento;
  /** Días sueltos: los tramos de un pedido, cada uno con su inicio y su fin (la firma va en «firma»). */
  tramos?: Vacacion[];
}

export interface VacacionRes {
  vacaciones?: Vacacion[];
  colaboradores?: Colaborador[];
  saldos?: Saldo[];
  /** Solicitudes esperando respuesta que este usuario puede resolver. */
  pendientes?: Vacacion[];
  /** Periodos de la gente a la que este usuario le autoriza. */
  equipo?: Vacacion[];
  /** Lo decide el servidor: si autoriza a alguien o no. */
  puedeAutorizar?: boolean;
  /** Días festivos del año, en ISO, para contar igual que el servidor. */
  festivos?: string[];
  /** Sólo el seguimiento: con qué alcance contestó el servidor. */
  alcance?: AlcanceSeguimiento;
  /** Sólo el seguimiento: los permisos del año, ya acotados al alcance. */
  permisos?: Permiso[];
  /** Al subir el FRC-019 firmado a mano: lo que quedó guardado. */
  firmado?: FrcFirmado;
  /** El historial del FRC-019 firmado, del último al primero (14/10/2026). */
  firmados?: FrcFirmado[];
  idGenerado?: number;
  /** Días sueltos: los ids de los tramos guardados, en orden; el primero es el del pedido. */
  ids?: number[];
  codigoError?: string;
  mensajeError?: string;
}

/** Una celda del calendario: un día del mes con lo que le toca. */
export interface DiaCalendario {
  /** ISO del día, o null cuando la celda solo rellena la cuadrícula. */
  iso: string | null;
  numero: number | null;
  esFinDeSemana: boolean;
  esHoy: boolean;
  /** Los periodos que cubren este día. Vacío si nadie descansa. */
  periodos: Vacacion[];
  /** Quien regresa a trabajar este día. Se marca distinto. */
  regresan: Vacacion[];
}

/**
 * Lo que se escribe cuando el campo pide algo y no hay nada que decir:
 * "NA", "N/A", "N.A.", "no aplica", "ninguno", "nada", "sin comentarios",
 * "S/C", o solo guiones, puntos o equis. Es la misma lista que el servidor
 * (VacacionBuss.NOTA_VACIA), que ya no los guarda; aquí se filtra lo que
 * se guardó antes del 14/09/2026, como el "NA" de Wendy.
 *
 * Va una regex por forma (cada una sencilla) en lugar de una sola con
 * alternativas, y "n / a" lleva el separador y su espacio en un solo grupo
 * opcional, como en el servidor: acepta lo mismo que n\s*[./]?\s*a sin que
 * los espacios se puedan repartir de dos maneras.
 */
const NOTA_VACIA: readonly RegExp[] = [
  /^n\s*(?:[./]\s*)?a\.?$/i,
  /^no\s+aplica\.?$/i,
  /^ningun[oa]?\.?$/i,
  /^nada\.?$/i,
  /^sin\s+comentarios?\.?$/i,
  /^s\s*\/\s*c$/i,
  /^[-._x?0\s]*$/i,
];

/** El comentario que vale la pena enseñar, o "" si está vacío o no dice nada. */
export function notaVisible(comentario?: string | null): string {
  const t = (comentario || "").trim();
  return NOTA_VACIA.some((patron) => patron.test(t)) ? "" : t;
}
